import React, { useState, useEffect } from 'react';
import { usePageTracking } from '../hooks/usePageTracking';
import { trackInitiateCheckout, track } from '../lib/tracking';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Check, Zap, Shield, Clock, Phone, FileText, 
  ArrowRight, CreditCard, Star, HelpCircle, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Stripe Payment Links - Direct checkout
const STRIPE_PAYMENT_LINKS = {
  assessment: 'https://buy.stripe.com/00w5kCevV077cse3Wd4F200',
  standard: 'https://buy.stripe.com/00w4gy87x6vv3VIgIZ4F203',
  priority: 'https://buy.stripe.com/4gM3cudrR8DD0Jw0K14F202'
};

const PricingPage = () => {
  usePageTracking("Pricing", { category: "pricing", value: 399 });
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Handle payment redirect with success/cancel params
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');

    if (success === 'true') {
      setPaymentStatus({ status: 'success', message: 'Payment successful! We will contact you shortly.' });
      toast.success('Payment successful! Check your email for confirmation.');
      // Fire Purchase event on all pixels (Stripe Payment Links don't support custom conversion tracking,
      // so we rely on the success redirect to fire the conversion event)
      track('Purchase', {
        currency: 'USD',
        content_type: 'product',
        source: 'stripe_payment_link_redirect',
      });
    } else if (cancelled === 'true') {
      setPaymentStatus({ status: 'cancelled', message: 'Payment was cancelled.' });
      track('CheckoutCancelled', { source: 'stripe_payment_link_redirect' });
    }
  }, [searchParams]);

  // Handle Stripe Payment Link checkout
  const handleCheckout = (planId) => {
    const paymentLink = STRIPE_PAYMENT_LINKS[planId];
    if (paymentLink) {
      const plan = plans.find((p) => p.id === planId);
      trackInitiateCheckout({ tier: plan?.name || planId, priceUsd: plan?.price || 0 });
      window.location.href = paymentLink;
    } else {
      toast.error('Payment link not available');
    }
  };

  const plans = [
    {
      id: 'assessment',
      name: 'Case Assessment',
      price: 99,
      originalPrice: null,
      promo: null,
      timeline: 'Evaluation: 3 Business Days',
      description: 'We prepare, you submit and follow up',
      features: [
        'Complete case analysis',
        'Policy violation review',
        'Evidence assessment',
        'Written evaluation report',
        'Full action playbook',
        'You submit through official channels'
      ],
      recommended: false,
      isAssessment: true,
      upgradeNote: 'Or upgrade to Full Service for comprehensive support'
    },
    {
      id: 'standard',
      name: 'Full Service',
      price: 399,
      originalPrice: null,
      promo: 'Best Value',
      timeline: 'Documentation: 5-7 Business Days',
      description: 'We prepare, we submit, we follow up',
      features: [
        'Everything in Assessment, plus:',
        'Professionally reviewed documentation',
        'We handle submission',
        'Case tracking with unique ID',
        'Email support',
        'Follow-up documentation (if denied)'
      ],
      recommended: true
    },
    {
      id: 'priority',
      name: 'Priority',
      price: 499,
      originalPrice: null,
      promo: 'Fastest Prep',
      timeline: 'Documentation: 24 Hours',
      description: 'Expedited preparation with priority support',
      features: [
        'Everything in Full Service, plus:',
        'Expedited 24-hour preparation',
        'Priority support',
        'Direct phone support line',
        'Priority processing',
        'Dedicated case manager'
      ],
      recommended: false
    }
  ];

  // Payment status display
  if (paymentStatus) {
    return (
      <div className="min-h-screen bg-[#1a1c1b]">
        <Header />
        <main className="pt-32 pb-16">
          <div className="max-w-xl mx-auto px-5 md:px-10">
            <div className={`bg-[#302f2c] border rounded-2xl p-8 text-center ${
              paymentStatus.status === 'success' ? 'border-green-500' : 
              paymentStatus.status === 'cancelled' ? 'border-yellow-500' : 'border-red-500'
            }`}>
              {paymentStatus.status === 'success' ? (
                <>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-black text-[#d9fb06] mb-2">Payment Successful!</h2>
                  <p className="text-[#dfddd6] mb-4">{paymentStatus.message}</p>
                  <div className="space-y-4">
                    <p className="text-[#888680]">
                      Next step: Complete your <Link to="/#intake" className="text-[#d9fb06] hover:underline">intake form</Link> to start your case.
                    </p>
                    <Link to="/#intake">
                      <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold">
                        Complete Intake Form
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </>
              ) : paymentStatus.status === 'cancelled' ? (
                <>
                  <XCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-black text-yellow-500 mb-2">Payment Cancelled</h2>
                  <p className="text-[#888680] mb-6">{paymentStatus.message}</p>
                  <Button 
                    onClick={() => { setPaymentStatus(null); window.history.replaceState({}, '', '/pricing'); }}
                    className="bg-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]/80"
                  >
                    Try Again
                  </Button>
                </>
              ) : (
                <>
                  <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-black text-red-500 mb-2">Payment Issue</h2>
                  <p className="text-[#888680] mb-6">{paymentStatus.message}</p>
                  <div className="space-y-2">
                    <Button 
                      onClick={() => { setPaymentStatus(null); window.history.replaceState({}, '', '/pricing'); }}
                      className="bg-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]/80"
                    >
                      Try Again
                    </Button>
                    <p className="text-[#888680] text-sm">
                      Need help? Call <a href="tel:808-301-2134" className="text-[#d9fb06]">808-301-2134</a>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#d9fb06] uppercase leading-[0.9] mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg md:text-xl text-[#888680] max-w-2xl mx-auto">
              Choose your package and get started immediately. No hidden fees, no surprises.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {plans.map((plan) => (
              <div 
                key={plan.id}
                className={`relative bg-[#302f2c] border rounded-2xl p-6 transition-all ${
                  plan.recommended 
                    ? 'border-[#d9fb06] shadow-lg shadow-[#d9fb06]/10' 
                    : plan.isAssessment
                    ? 'border-blue-500/50'
                    : 'border-[#3f4816]/50'
                }`}
              >
                {/* Recommended Badge */}
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#d9fb06] text-[#1a1c1b] px-4 py-1 rounded-full text-sm font-bold uppercase">
                      Best Value
                    </span>
                  </div>
                )}

                {/* Assessment Badge */}
                {plan.isAssessment && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-bold uppercase">
                      Low Risk Start
                    </span>
                  </div>
                )}

                {/* Promo Badge */}
                {plan.promo && (
                  <div className="inline-flex items-center gap-1 px-3 py-1 bg-[#d9fb06]/20 rounded-full mb-4">
                    <Star className="w-4 h-4 text-[#d9fb06]" />
                    <span className="text-sm font-medium text-[#d9fb06]">{plan.promo}</span>
                  </div>
                )}

                {/* Plan Name & Timeline */}
                <h2 className={`text-2xl font-black mb-2 ${plan.isAssessment ? 'text-blue-400' : 'text-[#dfddd6]'}`}>
                  {plan.name}
                </h2>
                <p className="text-sm text-[#888680] mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {plan.timeline}
                </p>

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`text-4xl font-black ${plan.isAssessment ? 'text-blue-400' : 'text-[#d9fb06]'}`}>
                      ${plan.price}
                    </span>
                    {plan.originalPrice && (
                      <span className="text-lg text-[#888680] line-through">${plan.originalPrice}</span>
                    )}
                  </div>
                  <p className="text-sm text-[#888680] mt-1">One-time payment</p>
                </div>

                {/* Description */}
                <p className="text-[#888680] mb-4 text-sm">{plan.description}</p>

                {/* Upgrade Note for Assessment */}
                {plan.upgradeNote && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
                    <p className="text-xs text-blue-400 text-center">
                      {plan.upgradeNote}
                    </p>
                  </div>
                )}

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={`${plan.id}-feature-${idx}`} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.isAssessment ? 'text-blue-400' : 'text-[#d9fb06]'}`} />
                      <span className="text-xs text-[#dfddd6]">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button - Direct Stripe Payment Link */}
                <Button
                  onClick={() => handleCheckout(plan.id)}
                  className={`w-full py-5 font-bold text-base ${
                    plan.recommended
                      ? 'bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90'
                      : plan.isAssessment
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]/80'
                  }`}
                  data-testid={`checkout-${plan.id}-btn`}
                >
                  {plan.isAssessment ? 'Get Started - $99' : `Buy Now - $${plan.price}`}
                  <CreditCard className="w-5 h-5 ml-2" />
                </Button>
              </div>
            ))}
          </div>

          {/* Platform Response Timeline Disclaimer */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-[#1a1c1b] border border-[#3f4816]/50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-[#888680] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-[#dfddd6] font-bold mb-2">Important: Platform Response Times</h4>
                  <p className="text-sm text-[#888680] leading-relaxed mb-3">
                    The timelines above refer to <span className="text-[#dfddd6]">our documentation preparation time</span>. 
                    Once submitted, platform response times are outside our control.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div className="bg-[#302f2c] rounded-lg p-3">
                      <p className="text-[#d9fb06] font-medium">Our Documentation Preparation</p>
                      <p className="text-[#888680]">Per plan timeline (2-7 days)</p>
                    </div>
                    <div className="bg-[#302f2c] rounded-lg p-3">
                      <p className="text-[#d9fb06] font-medium">Platform Response</p>
                      <p className="text-[#888680]">Varies - outside our control</p>
                    </div>
                  </div>
                  <p className="text-xs text-[#888680] mt-3 italic">
                    Response times depend on how many disputes each platform receives during that period. 
                    We cannot guarantee or expedite platform review times.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Refund Guarantee Banner */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-gradient-to-r from-[#3f4816]/50 to-[#302f2c] border border-[#d9fb06]/30 rounded-2xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-[#d9fb06] flex items-center justify-center flex-shrink-0">
                    <Shield className="w-8 h-8 text-[#1a1c1b]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-[#d9fb06] uppercase">Refund Policy</h3>
                    <p className="text-[#888680] mt-1">Partial refunds may be available. See refund policy.</p>
                  </div>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-sm text-[#888680]">
                    See <Link to="/refund-policy" className="text-[#d9fb06] hover:underline">refund policy</Link> for details
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-black text-[#d9fb06] uppercase text-center mb-8">
              Common Questions
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-6">
                <h4 className="text-[#dfddd6] font-bold mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#d9fb06]" />
                  What if my case isn't successful?
                </h4>
                <p className="text-sm text-[#888680]">
                  We include a follow-up letter template at no extra charge. If denied, you may purchase 
                  additional follow-up services. See our <Link to="/refund-policy" className="text-[#d9fb06] hover:underline">refund policy</Link> for details.
                </p>
              </div>
              <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-6">
                <h4 className="text-[#dfddd6] font-bold mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#d9fb06]" />
                  Do I need to fill out the intake form?
                </h4>
                <p className="text-sm text-[#888680]">
                  Yes, after purchase you'll need to complete the <Link to="/#intake" className="text-[#d9fb06] hover:underline">intake form</Link> so 
                  we can gather your case details. This helps us prepare the strongest documentation for your situation.
                </p>
              </div>
              <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-6">
                <h4 className="text-[#dfddd6] font-bold mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#d9fb06]" />
                  Which platforms do you support?
                </h4>
                <p className="text-sm text-[#888680]">
                  Instagram, Facebook, TikTok, YouTube, X (Twitter), LinkedIn, Reddit, Snapchat, Uber Eats, and more. 
                  <Link to="/contact" className="text-[#d9fb06] hover:underline ml-1">Contact us</Link> for other platforms.
                </p>
              </div>
              <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-6">
                <h4 className="text-[#dfddd6] font-bold mb-2 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#d9fb06]" />
                  What's the difference between packages?
                </h4>
                <p className="text-sm text-[#888680]">
                  Priority includes expedited preparation and priority support. 
                  Standard includes documentation preparation with email support.
                </p>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-16 text-center">
            <div className="flex flex-wrap items-center justify-center gap-8">
              <div className="flex items-center gap-2 text-[#888680]">
                <Shield className="w-5 h-5 text-[#d9fb06]" />
                <span className="text-sm">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2 text-[#888680]">
                <Check className="w-5 h-5 text-[#d9fb06]" />
                <span className="text-sm">Professionally Reviewed</span>
              </div>
              <div className="flex items-center gap-2 text-[#888680]">
                <Zap className="w-5 h-5 text-[#d9fb06]" />
                <span className="text-sm">USA Based</span>
              </div>
            </div>
          </div>

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PricingPage;
